import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Search } from "@mui/icons-material";
import DocumentTable from "./DocumentTable";
import { compareByFileName } from "./documentListUtils";

const normalize = (value) => String(value || "").trim().toLowerCase();

/**
 * Library picker. Documents come directly from the Auth-context cache via the
 * `libraryDocuments` prop — no per-open fetch. Already-attached docs are greyed
 * and locked.
 */
const DocumentLibraryDialog = ({
  open,
  onClose,
  promptId,
  title = "Add documents from your library",
  emptyMessage = "No documents in your library yet. Upload one first to make it available here.",
  libraryDocuments = [],
  loading = false,
  attachToPrompt,
  onAttached,
  getExtraChips,
}) => {
  const normalizedPromptId = normalize(promptId);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState(null);
  // Set only while not-yet-embedded docs are being ingested (extract + chunk +
  // embed per document), which can take a while for large batches.
  const [importProgress, setImportProgress] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setSearch("");
    setAttachError(null);
    setImportProgress(null);
  }, [open]);

  const filteredDocuments = useMemo(() => {
    const needle = normalize(search);
    const sorted = [...libraryDocuments].sort(compareByFileName);
    if (!needle) return sorted;
    return sorted.filter((doc) => {
      const haystacks = [
        doc.fileName,
        doc.metadata?.source,
        doc.metadata?.author,
        doc.metadata?.documentType,
        doc.metadata?.specialty,
      ];
      return haystacks.some((value) => normalize(value).includes(needle));
    });
  }, [libraryDocuments, search]);

  const isAttached = (doc) => {
    const ids = Array.isArray(doc.promptIds) ? doc.promptIds : [];
    return ids.map(normalize).includes(normalizedPromptId);
  };

  const toggleOne = (docId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleAttach = async () => {
    if (!normalizedPromptId) {
      setAttachError("A prompt ID is required.");
      return;
    }
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setAttaching(true);
    setAttachError(null);
    setImportProgress(null);
    try {
      const result = await attachToPrompt(ids, normalizedPromptId, setImportProgress);
      const errors = Array.isArray(result?.errors) ? result.errors : [];
      const attachedCount = Array.isArray(result?.attached)
        ? result.attached.length
        : ids.length - errors.length;
      if (errors.length && attachedCount === 0) {
        setAttachError(
          `Failed to attach: ${errors.map((e) => e.error || e.docId).join(", ")}`,
        );
        return;
      }
      onAttached?.({ attachedCount, errors });
      onClose?.();
    } catch (err) {
      console.error("[DocumentLibraryDialog] attachToPrompt failed:", err);
      setAttachError(err?.message || "Failed to attach documents.");
    } finally {
      setAttaching(false);
      setImportProgress(null);
    }
  };

  const selectedCount = selectedIds.size;

  return (
    <Dialog open={open} onClose={attaching ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search by file name, source, author, or specialty"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          fullWidth
        />
        {attachError ? <Alert severity="error">{attachError}</Alert> : null}
        <DocumentTable
          documents={filteredDocuments}
          loading={loading}
          emptyMessage={
            libraryDocuments.length ? "No documents match your search." : emptyMessage
          }
          selection={{ selectedIds, onToggle: toggleOne }}
          isRowDisabled={isAttached}
          {...(getExtraChips ? { getExtraChips } : {})}
          getRowDisabledChip={(doc) =>
            isAttached(doc)
              ? {
                  label: "Already attached",
                  color: "success",
                  variant: "outlined",
                }
              : null
          }
          rowClickToggles
          maxHeight={480}
        />
      </DialogContent>
      {attaching && importProgress?.total ? (
        <Box sx={{ px: 3, pt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(importProgress.done / importProgress.total) * 100}
            sx={{ height: 6, borderRadius: 1 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            Embedding document{" "}
            {Math.min(importProgress.done + 1, importProgress.total)} of{" "}
            {importProgress.total}. New documents are embedded one at a time, so
            large batches can take a few minutes. Please keep this dialog open.
          </Typography>
        </Box>
      ) : null}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: "auto" }}>
          {selectedCount} selected
        </Typography>
        <Button onClick={onClose} disabled={attaching}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleAttach}
          loading={attaching}
          disabled={attaching || selectedCount === 0}
        >
          {selectedCount > 0
            ? `Attach ${selectedCount} ${selectedCount === 1 ? "document" : "documents"}`
            : "Attach"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentLibraryDialog;
